import moment from 'moment';

import { GanttColumnTypesEnum } from 'src/pages/gantt/gantt.constants';

import {
  COLUMN_HEIGHT,
  COLUMN_WIDTH,
  DATE_COLUMN_WIDTH,
  DATE_FORMAT_KEY,
  HEADER_ROW_HEIGHT,
} from './gantt-view.constants';
import { IViewRange, IViewDateMap, IViewDate, IGanttData } from './gantt-view.interfaces';

export const getColumnTypes = ({
  currentDate,
  targetDate,
  companyWeekends = [],
  holidaysMapByDate,
}: any) => {
  const types = [];
  const dateInFormat = targetDate.format(DATE_FORMAT_KEY);
  const currentDateInFormat = currentDate.format(DATE_FORMAT_KEY);
  const weekDay = targetDate.format('dddd').toLowerCase();
  const dayDiff = currentDate.diff(targetDate, 'd', true);
  const isWeekend = companyWeekends.includes(weekDay);

  if (currentDateInFormat === dateInFormat) {
    types.push(GanttColumnTypesEnum.CURRENT_DAY);
  }

  if (dayDiff < 0) {
    types.push(GanttColumnTypesEnum.FUTURE_DAY);
  }

  if (isWeekend) {
    types.push(GanttColumnTypesEnum.WEEKEND);
  }

  if (holidaysMapByDate.includes(dateInFormat)) {
    types.push(GanttColumnTypesEnum.HOLYDAY);
  }

  return types;
};

export const getHolidaysMapDate = (holidaySubGroups: any) => {
  const holidays = holidaySubGroups.flatMap(({ holidays }: any) => holidays);
  const holidaysMapByDate = new Set();

  holidays.forEach(({ dateTo, dateFrom }: any) => {
    for (
      const dateItem = moment(new Date(dateFrom));
      dateItem.isSameOrBefore(new Date(dateTo));
      dateItem.add(1, 'day')
    ) {
      const dateInFormat = dateItem.format(DATE_FORMAT_KEY);

      holidaysMapByDate.add(dateInFormat);
    }
  });

  return Array.from(holidaysMapByDate);
};

export const getDateMapFromViewRange = (
  { startDate, endDate }: IViewRange,
  companyWeekends: any,
  ganttControl: any,
  holidaysMapByDate: any
): IViewDateMap => {
  let index = 0;
  const dateMap: IViewDateMap = {};
  const currentDate = moment();

  for (const dateItem = moment(startDate); dateItem.isBefore(endDate); dateItem.add(1, 'day')) {
    const dateInFormat = dateItem.format(DATE_FORMAT_KEY);
    const types = getColumnTypes({
      currentDate,
      targetDate: dateItem,
      companyWeekends,
      holidaysMapByDate,
    });

    if (!ganttControl.weekendsActive && types.includes(GanttColumnTypesEnum.WEEKEND)) {
      continue;
    }

    dateMap[dateInFormat] = {
      index,
      dateInFormat,
      date: dateItem.toDate(),
      workingHours: 0,
      weekDay: dateItem.format('dddd').toLowerCase(),
      types,
    };

    index += 1;
  }

  return dateMap;
};

export const getDateViewInfoFromViewRange = (
  viewRange: any,
  companyWeekends: any,
  ganttControl: any,
  holidaySubGroups: any
) => {
  const holidaysMapByDate = getHolidaysMapDate(holidaySubGroups);
  const dateMapByDate = getDateMapFromViewRange(
    viewRange,
    companyWeekends,
    ganttControl,
    holidaysMapByDate
  );
  const rangeMoreThenMonth = moment(viewRange.startDate).diff(viewRange.endDate, 'M') < 0;

  return {
    dateMapByDate,
    holidaysMapByDate,
    dateValues: Object.values(dateMapByDate),
    viewColumnDateFormat: rangeMoreThenMonth ? 'ddd DD/MM' : 'ddd DD',
  };
};

export const getGanttGridInfo = (
  ganttData: IGanttData[],
  containerWidth: number,
  dateValues?: IViewDate[]
) => {
  const fullWidth = ganttData.length * COLUMN_WIDTH + DATE_COLUMN_WIDTH;
  let fullHeight = 0;

  if (dateValues) {
    fullHeight = dateValues.length * COLUMN_HEIGHT + HEADER_ROW_HEIGHT;
  }

  return {
    fullWidth: fullWidth > containerWidth ? fullWidth : containerWidth,
    fullHeight,
  };
};

export const getNewEmployeeIdsOrder = ({ employeeId, newOrder, ganttEmployeeIds }: any) => {
  const newEmployeeIdsOrder = [...ganttEmployeeIds];
  const findIndex = ganttEmployeeIds.findIndex((id: string) => id === employeeId);

  // skip update if element is on then top and sort action is up
  if (findIndex === 0 && newOrder < 0) return null;

  // skip update if element is on then bottom and sort action is down
  if (findIndex === ganttEmployeeIds.length - 1 && newOrder > 0) return null;

  newEmployeeIdsOrder.splice(findIndex, 1);
  newEmployeeIdsOrder.splice(findIndex + newOrder, 0, employeeId);

  return newEmployeeIdsOrder;
};

export const getEntryByPointPosition = ({
  pointX,
  pointY,
  ganttData,
  dateValues,
}: {
  pointX: number;
  pointY: number;
  ganttData: any;
  dateValues: any;
}) => {
  const { employee, ganttRecordsByDateMap = {} } =
    ganttData.find((_: any, index: number) => {
      const employeeXStart = index * COLUMN_WIDTH + DATE_COLUMN_WIDTH;
      const employeeXEnd = index * COLUMN_WIDTH + DATE_COLUMN_WIDTH + COLUMN_WIDTH;

      return employeeXStart <= pointX && employeeXEnd >= pointX;
    }) || {};

  const date = dateValues.find((_: any, index: number) => {
    const dateYStart = index * COLUMN_HEIGHT + HEADER_ROW_HEIGHT;
    const dateYEnd = index * COLUMN_HEIGHT + HEADER_ROW_HEIGHT + COLUMN_HEIGHT;

    return dateYStart <= pointY && dateYEnd >= pointY;
  });

  return { employee, date, record: date ? ganttRecordsByDateMap[date.dateInFormat] : null };
};
